import React, { useEffect, useState, useMemo } from "react";
import { History, RotateCcw, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { theses as thesesApi } from "@/lib/api";
import { toast } from "sonner";
import NumaLogo from "@/components/NumaLogo";
import { sanitizeHtml } from "@/lib/sanitize";
import AiBadge from "@/components/AiBadge";

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "adesso";
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h fa`;
  const d = Math.floor(h / 24);
  return `${d} g fa`;
}

const SOURCE_LABEL = {
  autosave: "Salvataggio automatico",
  manual: "Salvataggio manuale",
  import: "Versione importata",
  "pre-restore": "Pre-ripristino",
  "manual-restore": "Ripristino",
};

export default function HistoryView({ thesis, onChapterUpdate }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [restoringId, setRestoringId] = useState(null);

  const load = async () => {
    if (!thesis?.thesis_id) return;
    setLoading(true);
    try {
      const res = await thesesApi.history(thesis.thesis_id);
      setVersions(res.versions || []);
    } catch {
      toast.error("Errore caricamento cronologia");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [thesis?.thesis_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => {
    const byChapter = {};
    versions.forEach((v) => {
      if (!byChapter[v.chapter_id]) byChapter[v.chapter_id] = [];
      byChapter[v.chapter_id].push(v);
    });
    return thesis?.chapters
      ?.slice()
      ?.sort((a, b) => a.order - b.order)
      ?.map((c) => ({ chapter: c, versions: byChapter[c.id] || [] })) || [];
  }, [versions, thesis]);

  const openPreview = async (v) => {
    try {
      const full = await thesesApi.getVersion(thesis.thesis_id, v.version_id);
      setPreview(full);
    } catch {
      toast.error("Errore apertura versione");
    }
  };

  const restore = async (v) => {
    setRestoringId(v.version_id);
    try {
      const updated = await thesesApi.restoreVersion(thesis.thesis_id, v.chapter_id, v.version_id);
      onChapterUpdate?.(updated);
      toast.success("Versione ripristinata");
      await load();
      setPreview(null);
    } catch {
      toast.error("Errore ripristino");
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div className="h-screen overflow-y-auto bg-surface numa-fade-in" data-testid="history-view">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 pt-8 lg:pt-14 pb-24">
        <div className="flex items-center gap-3 mb-2">
          <NumaLogo size={8} className="" />
          <p className="text-xs uppercase tracking-widest text-assistant font-medium">Cronologia</p>
        </div>
        <h1 className="font-serif-display text-3xl sm:text-4xl font-semibold mt-1 text-ink">Versioni dei capitoli</h1>
        <AiBadge mode="none" className="mt-3"
          detail="Storico delle tue modifiche, salvato sul server. Nessun modello coinvolto." />
        <p className="mt-2 max-w-3xl text-ink-muted">
          Ogni salvataggio è conservato: puoi tornare a una versione precedente quando vuoi.
        </p>

        {loading && (
          <div className="mt-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-assistant" />
          </div>
        )}

        {!loading && versions.length === 0 && (
          <div className="mt-12 border-2 border-dashed border-line rounded-md p-12 text-center bg-surface">
            <History className="w-10 h-10 mx-auto text-assistant mb-3" />
            <p className="text-sm text-ink-muted">Nessuna versione salvata. Inizia a scrivere e ogni autosave creerà una versione.</p>
          </div>
        )}

        <div className="mt-8 space-y-8">
          {!loading && groups.map((g) => (
            <div key={g.chapter.id} data-testid={`history-group-${g.chapter.order}`}>
              <div className="flex items-baseline justify-between border-b border-line pb-2 mb-3">
                <h2 className="font-serif-display text-lg font-semibold text-ink">{g.chapter.title}</h2>
                <span className="text-xs text-ink-muted">{g.versions.length} versioni</span>
              </div>
              {g.versions.length === 0 ? (
                <p className="text-sm text-ink-subtle py-2">Nessuna versione per questo capitolo.</p>
              ) : (
                <ol className="relative border-l border-line ml-3 space-y-3">
                  {g.versions.map((v, idx) => (
                    <li key={v.version_id} className="pl-5 relative">
                      <span className={`absolute -left-[7px] top-2 w-3.5 h-3.5 rounded-full border-2 border-surface ${idx === 0 ? "bg-brand" : "bg-line-strong"}`} />
                      <div className="bg-surface border border-line rounded-md p-4 hover:border-line-strong hover:shadow-sm transition-all">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-[10px] uppercase tracking-widest text-assistant font-medium">{SOURCE_LABEL[v.source] || v.source}</span>
                          <span className="text-xs text-ink-muted">{timeAgo(v.created_at)}</span>
                          <span className="text-xs text-ink-subtle">·</span>
                          <span className="text-xs text-ink-muted">{v.word_count || 0} parole</span>
                          {idx === 0 && <span className="text-[10px] bg-assistant-soft text-assistant px-2 py-0.5 rounded-full font-medium">Più recente</span>}
                        </div>
                        <div className="mt-3 flex gap-2">
                          <Button
                            data-testid={`view-version-${v.version_id}`}
                            variant="outline"
                            size="sm"
                            onClick={() => openPreview(v)}
                            className="rounded-md border-line"
                          >
                            <FileText className="w-3.5 h-3.5 mr-1.5" /> Visualizza
                          </Button>
                          <Button
                            data-testid={`restore-version-${v.version_id}`}
                            size="sm"
                            onClick={() => restore(v)}
                            disabled={restoringId === v.version_id || idx === 0}
                            className="disabled:opacity-50"
                          >
                            {restoringId === v.version_id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
                            Ripristina
                          </Button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="version-preview-dialog" className="max-w-[95vw] sm:max-w-2xl rounded-md">
          <DialogHeader>
            <DialogTitle className="font-serif-display">
              {preview?.title} · <span className="text-ink-muted text-base font-normal">{timeAgo(preview?.created_at)}</span>
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div
              className="numa-editor prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(preview?.content) || "<p><i>(vuoto)</i></p>" }}
            />
          </ScrollArea>
          <div className="flex justify-end gap-2 pt-3 border-t border-line">
            <Button variant="outline" onClick={() => setPreview(null)} className="rounded-md border-line">Chiudi</Button>
            <Button
              onClick={() => restore(preview)}
              disabled={restoringId === preview?.version_id}
              
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Ripristina questa versione
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
